<template>
  <v-flex
    v-if="!loading"
    xs8
    offset-xs2
    align-self-center
    justify-center
  >
  <div>
    <v-container fill-height fluid>
    <v-row>
      <v-flex xs3 class="mr-2">
        <Search
          v-model="filters.search"
          :label="$t('SEARCH')"
          prepend-icon="search"
          :delay="500"
        />
      </v-flex>
      <v-flex xs class="ml-2">
        <PropagandistSelect
          :clearable="true"
          v-model="filters.propagandist_id"
          :label="$t('fields.propagandist')"
        />
      </v-flex>
      <v-spacer></v-spacer>
      <v-btn
        v-if="can('create schools')"
        @click="showSchoolModal(0)"
        color="primary"
        dark
        class="mb-2 mr-2"
      >{{ $t("NEW") }}
      </v-btn>
      <v-btn
        v-if="can('export schools')"
        @click="exportData()"
        dark
        class="mb-2"
      >{{ $t("EXPORT") }}
      </v-btn>
    </v-row>
    </v-container>
    <v-data-table
      :headers="headers"
      :items="items"
      :server-items-length="totalItems"
      :no-data-text="$t('NO_DATA')"
      :no-results-text="$t('NO_DATA')"
      hide-default-footer
      :options.sync="pagination"
      :loading="loading"
      class="elevation-1"
    >
      <template v-slot:item.name="{ item }">
        <router-link :to="{ name: routeName.SCHOOL_SHOW, params: { id: item.id } }">
            {{ item.name }}
        </router-link>
      </template>

      <template v-slot:item.code="{ item }">
            {{ item.code }}
      </template>
      <template v-slot:item.actions="{ item }">
            <v-layout row wrap>
              <v-flex class="text-no-wrap">
                <v-btn
                  icon
                  color="blue"
                  v-if="can('edit schools')"
                  @click="showSchoolModal(item.id)"
                >
                  <v-icon>edit</v-icon>
                </v-btn>
                <v-btn
                  v-if="can('delete schools')"
                  icon
                  color="red"
                  @click="deleteSchool(item.id)"
                >
                  <v-icon>cancel</v-icon>
                </v-btn>
              </v-flex>
            </v-layout>
      </template>
    </v-data-table>
    <div class="pt-2">{{$t('fields.total_items')}} {{ totalItems }}</div>
    <Paginator
      :total-items="totalItems"
      :page.sync="pagination.page"
      :items-per-page.sync="pagination.itemsPerPage"
      @change="fetchSchools"
    />
  </div>
  </v-flex>
  <Loader v-else/>
</template>
<script>
  import { mapActions } from 'vuex';

  import { schoolsService } from '../../utils/services/schools.service';
  import Search from '../../components/shared/Inputs/Search';
  import Loader from '../../components/shared/Loader';
  import SchoolModal from '../../components/modals/Schools/SchoolModal';
  import PropagandistSelect from '../../components/shared/Vselects/PropagandistSelect.vue';
  import Paginator from '../../components/shared/Paginator';

  export default {
    name: 'SchoolList',
    components: { Search, Loader, PropagandistSelect, Paginator },
    data() {
      return {
        loading: false,
        items: [],
        totalItems: 0,
        pagination: {
          page: 1,
          itemsPerPage: 10,
          sortDesc: [false],
          sortBy: ['name'],
        },
        headers: [
          { text: this.$t('fields.code'), value: 'code' },
          { text: this.$t('fields.name'), value: 'name' },
          { text: this.$t('fields.type'), value: 'schools_types_name' },
          { text: this.$t('fields.city'), value: 'cities' },
          { text: this.$t('fields.propagandist'), value: 'propagandist' },
          { text: this.$t('fields.actions'), value: 'actions', sortable: false, align: 'center' },
        ],
        filters: {
          search: null,
          propagandist_id: 0,
        },
      };
    },
    computed: {
      requestFilters() {
        const filters = {
          search: this.filters.search,
          propagandist_id: this.filters.propagandist_id,
        };
        if (this.pagination.sortBy.length === 1 && this.pagination.sortDesc.length === 1) {
          const { sortBy, sortDesc } = this.pagination;
          filters.sortBy = sortBy;
          filters.sortDesc = sortDesc;
        }
        return filters;
      },
    },
    methods: {
      ...mapActions('confirm', ['showConfirm']),
      ...mapActions('modal', ['showModal', 'hideModal']),
      ...mapActions('alert', ['showToast']),
      async fetchSchools(forcePage) {
        this.loading = true;
        const { page, itemsPerPage } = this.pagination;
        return schoolsService.index({
          page: page || forcePage || 1,
          itemsPerPage,
          filters: this.requestFilters,
        })
          .then(response => {
            this.items = response.data.data;
            this.totalItems = response.data.meta.total;
            this.pagination = this.getPagination(
              response.data.meta,
              { oldPagination: this.pagination, forcePage },
            );
          })
          .catch(error => {
            this.handleError(error, this.errors);
          })
          .finally(() => {
            this.loading = false;
          });
      },
      exportData() {
        this.loading = true;
        return schoolsService.exportData({
          filters: this.requestFilters,
        })
          .then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'schools.csv');
            document.body.appendChild(link);
            link.click();
          })
          .catch(error => {
            this.handleError(error, this.errors);
          })
          .finally(() => {
            this.loading = false;
          });
      },
      async showSchoolModal(id) {
        this.showModal({
          component: SchoolModal,
            data: {
              stored: {
                id,
              },
          },
          options: {
            title: this.$t('SCHOOL'),
            onSave: (
              form,
              error,
            ) => schoolsService.save(form)
            .then(() => {
              this.hideModal({});
              this.showToast({ type: 'success', message: this.$t('SAVED') });
            })
            .catch(error => {
              this.handleError(error, this.errors);
            })
            .finally(() => {
              this.fetchSchools(this.pagination.page);
            }),
          },
        });
      },
      async deleteSchool(id) {
          this.showConfirm({
            onConfirm: () => {
              this.loading = true;
              return schoolsService.remove(id)
              .then(() => {
                this.showToast({ type: 'success', message: this.$t('DELETED') });
                this.fetchSchools(this.pagination.page);
              })
              .catch(error => {
                this.handleError(error, this.errors);
              })
              .finally(() => {
                this.loading = false;
              });
            },
          });
      },
    },
    async created() {
      await this.fetchSchools(this.pagination.page);
    },
    watch: {
      filters: {
        deep: true,
        handler() {
          this.fetchSchools(1);
        },
      },
      'pagination.sortBy': {
        handler(oldVal, newVal) {
          if (newVal !== oldVal) {
            this.fetchSchools(1);
          }
        },
      },

    },
  };
</script>
